// Admin · Reviews — verify order numbers, approve / reject submissions.
// Auth: shared operator password (same token key as other admin pages).
import { useCallback, useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { API_BASE } from '../lib/apiBase';

const TOKEN_KEY = 'loadout.admin.community.token';
const TABS = ['pending', 'approved', 'rejected'];

function readToken() {
        try { return window.localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}

function Login({ onLogin }) {
        const [pw, setPw] = useState('');
        const [err, setErr] = useState('');
        const submit = async (e) => {
                e.preventDefault();
                setErr('');
                const r = await fetch(`${API_BASE}/api/admin/community/login`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: pw }),
                }).catch(() => null);
                if (!r || !r.ok) { setErr(r?.status === 401 ? 'Wrong password.' : 'Login failed.'); return; }
                onLogin((await r.json()).token);
        };
        return (
                <main data-testid="admin-reviews-login-page" className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
                        <form onSubmit={submit} className="w-full max-w-[420px] border border-white/15 p-6 space-y-4">
                                <p className="font-mono text-[11px] tracking-[0.18em] text-[#FF9F1C]">LOADOUT / ADMIN</p>
                                <h1 className="font-display text-[26px] uppercase">Reviews</h1>
                                <input data-testid="admin-reviews-login-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Operator password" autoFocus className="w-full bg-transparent border border-white/20 px-3 py-3 font-mono text-[13px] focus:outline-none focus:border-[#FF9F1C]" />
                                {err && <p data-testid="admin-reviews-login-error" className="font-mono text-[11px] text-red-400">{err}</p>}
                                <button data-testid="admin-reviews-login-submit" type="submit" className="w-full bg-[#FF9F1C] text-[#0A0A0A] font-mono text-[12px] tracking-[0.2em] uppercase py-3.5">Sign in</button>
                        </form>
                </main>
        );
}

function AdminImage({ src, token }) {
        const [url, setUrl] = useState('');
        useEffect(() => {
                let obj = '';
                fetch(`${API_BASE}${src}`, { headers: { Authorization: `Bearer ${token}` } })
                        .then((r) => (r.ok ? r.blob() : null))
                        .then((b) => { if (b) { obj = URL.createObjectURL(b); setUrl(obj); } })
                        .catch(() => {});
                return () => { if (obj) URL.revokeObjectURL(obj); };
        }, [src, token]);
        if (!url) return <div className="h-24 w-24 border border-white/15 bg-white/[0.03]" />;
        return (
                <a href={url} target="_blank" rel="noreferrer" data-testid="admin-review-image" className="block h-24 w-24 overflow-hidden border border-white/15 hover:border-[#FF9F1C]">
                        <img src={url} alt="Customer upload" className="h-full w-full object-cover" />
                </a>
        );
}

function ReviewCard({ r, onStatus, busy, token }) {
        return (
                <article data-testid="admin-review-card" className="border border-white/15 p-5">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                        <a href={`/p/${r.product_handle}`} target="_blank" rel="noreferrer" className="font-display text-[16px] uppercase hover:text-[#FF9F1C]">{r.product_title}</a>
                                        <div className="mt-1 flex gap-0.5">
                                                {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= r.rating ? 'fill-[#FF9F1C] text-[#FF9F1C]' : 'text-white/25'}`} />)}
                                        </div>
                                </div>
                                <div className="text-right font-mono text-[11px] text-white/60 space-y-0.5">
                                        <p data-testid="admin-review-order" className="text-[#FF9F1C] text-[13px]">Order #{r.order_number}</p>
                                        <p>{r.author_name} · {r.author_email}</p>
                                        <p>{r.created_at ? new Date(r.created_at).toLocaleString('en-IN') : ''}</p>
                                </div>
                        </div>
                        {r.title && <p className="mt-3 font-display text-[15px] uppercase">{r.title}</p>}
                        <p className="mt-1 font-sans text-[13px] text-white/75 whitespace-pre-line">{r.body}</p>
                        {r.images?.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                        {r.images.map((src) => <AdminImage key={src} src={src} token={token} />)}
                                </div>
                        )}
                        <div className="mt-4 flex gap-2">
                                {r.status !== 'approved' && <button type="button" disabled={busy} onClick={() => onStatus(r.id, 'approved')} data-testid="admin-review-approve" className="border border-emerald-500 text-emerald-400 font-mono text-[11px] tracking-[0.18em] uppercase px-4 py-2 hover:bg-emerald-500 hover:text-black transition-colors disabled:opacity-40">Approve</button>}
                                {r.status !== 'rejected' && <button type="button" disabled={busy} onClick={() => onStatus(r.id, 'rejected')} data-testid="admin-review-reject" className="border border-red-500 text-red-400 font-mono text-[11px] tracking-[0.18em] uppercase px-4 py-2 hover:bg-red-500 hover:text-black transition-colors disabled:opacity-40">Reject</button>}
                        </div>
                </article>
        );
}

export default function AdminReviews() {
        const [token, setToken] = useState(readToken);
        const [tab, setTab] = useState('pending');
        const [rows, setRows] = useState([]);
        const [busy, setBusy] = useState(false);

        const logout = useCallback(() => {
                setToken('');
                try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
        }, []);

        const load = useCallback(async () => {
                const r = await fetch(`${API_BASE}/api/admin/reviews?status=${tab}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null);
                if (r?.status === 401) { logout(); return; }
                if (r?.ok) setRows((await r.json()).reviews || []);
        }, [tab, token, logout]);

        useEffect(() => { if (token) load(); }, [token, load]);

        const setStatus = async (id, status) => {
                setBusy(true);
                await fetch(`${API_BASE}/api/admin/reviews/${id}/status`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ status }),
                }).catch(() => null);
                setBusy(false);
                load();
        };

        if (!token) {
                return <Login onLogin={(t) => { setToken(t); try { window.localStorage.setItem(TOKEN_KEY, t); } catch { /* ignore */ } }} />;
        }
        return (
                <main data-testid="admin-reviews-page" className="min-h-screen bg-[#0A0A0A] text-white px-6 py-10">
                        <div className="mx-auto max-w-[960px]">
                                <div className="flex items-center justify-between">
                                        <div>
                                                <p className="font-mono text-[11px] tracking-[0.18em] text-[#FF9F1C]">LOADOUT / ADMIN</p>
                                                <h1 className="mt-2 font-display text-[32px] uppercase">Reviews</h1>
                                                <p className="mt-1 font-sans text-[13px] text-white/55">Verify the order number in Shopify before approving. Approved reviews go live on the PDP.</p>
                                                <a href="/admin/restock-alerts" data-testid="admin-reviews-restock-link" className="mt-2 inline-block font-mono text-[11px] uppercase tracking-[0.16em] text-[#FF9F1C] hover:underline">Restock alerts & review emails →</a>
                                        </div>
                                        <button type="button" onClick={logout} data-testid="admin-reviews-logout" className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60 hover:text-white">Sign out</button>
                                </div>
                                <div className="mt-8 flex gap-2">
                                        {TABS.map((t) => (
                                                <button key={t} type="button" onClick={() => setTab(t)} data-testid={`admin-reviews-tab-${t}`} className={'font-mono text-[11px] uppercase tracking-[0.16em] border px-4 py-2 transition-colors ' + (tab === t ? 'border-[#FF9F1C] text-[#FF9F1C]' : 'border-white/20 text-white/70 hover:border-white/50')}>{t}</button>
                                        ))}
                                </div>
                                <div className="mt-6 space-y-4">
                                        {rows.length === 0 ? (
                                                <p data-testid="admin-reviews-empty" className="font-mono text-[12px] uppercase tracking-[0.18em] text-white/45">No {tab} reviews.</p>
                                        ) : rows.map((r) => <ReviewCard key={r.id} r={r} onStatus={setStatus} busy={busy} token={token} />)}
                                </div>
                        </div>
                </main>
        );
}
